import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  acceptPublicEventTicketTransfer,
  getPublicEventTicketTransfer,
} from '../../../services/eventApi';
import LoadingSpinner from '../../Common/LoadingSpinner';

const PublicEventTicketTransferPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getPublicEventTicketTransfer(token);
        if (!cancelled) setData(res.data?.data || null);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'Transfer not available');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const res = await acceptPublicEventTicketTransfer(token, { email: data?.toEmail });
      setDone(res.data?.data || null);
      toast.success('Transfer completed');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not accept transfer');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg p-6 text-center">
          <h1 className="text-lg font-semibold text-gray-900">Transfer unavailable</h1>
          <p className="mt-2 text-sm text-gray-600">{error || 'Invalid link'}</p>
        </div>
      </div>
    );
  }

  if (done || data.status === 'COMPLETED') {
    const ticket = done?.ticket || data.ticket || {};
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg p-6">
          <p className="text-xs font-medium uppercase tracking-wide text-green-700">Transfer complete</p>
          <h1 className="mt-1 text-xl font-semibold text-gray-900">{data.event?.name || 'Event ticket'}</h1>
          <p className="mt-3 text-sm text-gray-700">
            Ticket <span className="font-mono text-indigo-700">{ticket.code || data.ticketCode}</span> is now held by{' '}
            <strong>{ticket.holderName || data.toName}</strong>.
          </p>
          {ticket.validDaysLabel || data.validDaysLabel ? (
            <p className="mt-2 text-xs text-gray-500">{ticket.validDaysLabel || data.validDaysLabel}</p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-indigo-600">Ticket transfer</p>
          <h1 className="mt-1 text-xl font-semibold text-gray-900">{data.event?.name || 'Event'}</h1>
          <p className="text-sm text-gray-500 mt-1">Accept this ticket into your name.</p>
        </div>
        <dl className="text-sm space-y-2">
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Ticket</dt>
            <dd className="font-mono text-indigo-700">{data.ticketCode}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">From</dt>
            <dd className="text-gray-900">{data.fromName || '—'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">To</dt>
            <dd className="text-gray-900">{data.toName} ({data.toEmail})</dd>
          </div>
          {data.validDaysLabel ? (
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Validity</dt>
              <dd className="text-gray-900">{data.validDaysLabel}</dd>
            </div>
          ) : null}
        </dl>
        {data.status === 'PENDING' ? (
          <button
            type="button"
            disabled={busy}
            onClick={accept}
            className="w-full inline-flex justify-center items-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            Accept transfer
          </button>
        ) : (
          <p className="text-sm text-amber-800">Status: {data.status}</p>
        )}
      </div>
    </div>
  );
};

export default PublicEventTicketTransferPage;
